import type { ReactElement } from 'react'
import { useState, type FC } from 'react'

import { Button, Checkbox } from '@skytech/ui'
import FilterIcon from 'mdi-react/FilterIcon'
import FilterMenuIcon from 'mdi-react/FilterMenuIcon'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/components/i18n/Translation'

export type FiltersButtonOption = {
  value: string | number
  label: ReactElement
  isSelected: boolean
}

export const FiltersButton: FC<{
  options: FiltersButtonOption[]
  buttonText?: string
  className?: string
  onClick: (value: string) => void
  onClear: () => void
}> = ({ options, buttonText, className, onClick, onClear }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <div>
      {/* Dropdown Button */}
      <Button
        className={twJoin('relative m-0 flex justify-between bg-transparent p-0', className)}
        variant="tertiary"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        Icon={isOpen ? <FilterMenuIcon /> : <FilterIcon />}
      >
        {buttonText && <span className="ml-4">{buttonText}</span>}
      </Button>

      {/* Dropdown Options */}
      {isOpen && (
        <div
          role="menu"
          className="absolute right-2 z-10 flex w-[130px] w-full min-w-[170px] max-w-[180px] flex-col rounded-md border border-gray-300 bg-white px-2 pb-2 pt-4 text-left shadow-lg"
        >
          {options.map(({ value, label, isSelected }: FiltersButtonOption) => (
            <Checkbox
              id={value.toString()}
              key={value}
              label={label}
              checked={isSelected}
              onChange={() => {
                onClick(String(value))
              }}
            />
          ))}

          <div className="mt-2 flex justify-between">
            <Button
              variant="tertiary"
              className="m-0 !h-8 min-h-8 w-fit self-end p-0 pr-2"
              onClick={() => setIsOpen(false)}
            >
              <Translation id="Close" />
            </Button>
            {options.some(option => option.isSelected) && (
              <Button
                variant="tertiary"
                className="m-0 !h-8 min-h-8 w-fit self-end p-0 pr-2"
                onClick={onClear}
              >
                <Translation id="Clear" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
